import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, Activity, Wallet, TrendingUp, ShieldCheck, CreditCard } from 'lucide-react';

function HeroVisual() {
  const [index, setIndex] = useState(0);

  const cards = [
    {
      name: 'Radioclinic',
      accent: '#0ea5e9',
      gradId: 'chart-radio',
      path: 'M0,80 C40,60 70,90 110,55 C150,25 180,50 220,30 C255,15 280,35 300,20',
      badgeTitle: 'Imagerie & IA',
      badgeSubtitle: 'Aide au diagnostic',
      rows: [
        { label: 'Performance', val: '+38%', color: 'text-emerald-500', bg: 'bg-emerald-50', up: true },
        { label: 'Conformité RGPD', val: '100%', color: 'text-sky-500', bg: 'bg-sky-50', up: false },
      ],
    },
    {
      name: 'KIMIA',
      accent: '#8b5cf6',
      gradId: 'chart-kimia',
      path: 'M0,90 C35,85 60,50 100,55 C140,60 165,25 205,35 C245,45 275,18 300,25',
      badgeTitle: 'Paiement & Fintech',
      badgeSubtitle: 'Transactions sécurisées',
      rows: [
        { label: 'Transactions', val: '+52%', color: 'text-emerald-500', bg: 'bg-emerald-50', up: true },
        { label: 'Paiements sécurisés', val: '100%', color: 'text-violet-500', bg: 'bg-violet-50', up: false },
      ],
    },
  ];

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % 2), 4000);
    return () => clearInterval(id);
  }, []);

  const card = cards[index];
  const isKimia = card.name === 'KIMIA';
  const areaPath = card.path + ' L300,110 L0,110 Z';
  const gradUrl = 'url(#' + card.gradId + ')';

  return (
    <div className="relative w-full flex items-center justify-center py-6 min-h-[440px]" data-testid="hero-visual">
      {/* Soft glow behind */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[380px] h-[380px] rounded-full bg-gradient-to-tr from-sky-200/50 via-violet-200/40 to-emerald-200/50 blur-[90px]" />
      </div>

      <div className="relative w-full max-w-[400px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.96 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <div className="relative w-full max-w-[380px] mx-auto">
              {/* Main glass dashboard card */}
              <div className="glass-panel rounded-2xl p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-zinc-950 flex items-center justify-center">
                      {isKimia ? <Wallet className="w-4 h-4 text-white" /> : <Activity className="w-4 h-4 text-white" />}
                    </div>
                    <span className="font-outfit font-semibold text-sm text-zinc-900">{card.name}</span>
                  </div>
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-zinc-200" />
                    <span className="w-2 h-2 rounded-full bg-zinc-200" />
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  </div>
                </div>

                {/* Mini area chart */}
                <div className="relative h-28 mb-5">
                  <svg viewBox="0 0 300 110" className="w-full h-full" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id={card.gradId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={card.accent} stopOpacity="0.25" />
                        <stop offset="100%" stopColor={card.accent} stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <motion.path
                      d={card.path}
                      fill="none"
                      stroke={card.accent}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 1.4, ease: 'easeInOut' }}
                    />
                    <path d={areaPath} fill={gradUrl} />
                  </svg>
                </div>

                {/* Stat rows */}
                <div className="space-y-3">
                  {[card.rows[0], card.rows[1]].map((row, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white/70 border border-zinc-100">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-7 h-7 rounded-lg ${row.bg} flex items-center justify-center`}>
                          {row.up
                            ? <TrendingUp className={`w-3.5 h-3.5 ${row.color}`} />
                            : <ShieldCheck className={`w-3.5 h-3.5 ${row.color}`} />}
                        </div>
                        <span className="font-manrope text-xs text-zinc-600">{row.label}</span>
                      </div>
                      <span className="font-outfit font-semibold text-sm text-zinc-900">{row.val}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating badge */}
              <div className="glass-panel absolute -bottom-5 -left-3 sm:-left-5 rounded-2xl px-4 py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-sky-500 flex items-center justify-center">
                  {isKimia ? <CreditCard className="w-4 h-4 text-white" /> : <Activity className="w-4 h-4 text-white" />}
                </div>
                <div>
                  <p className="font-outfit font-semibold text-xs text-zinc-900">{card.badgeTitle}</p>
                  <p className="font-manrope text-[10px] text-zinc-500">{card.badgeSubtitle}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Dots indicator */}
        <div className="flex justify-center gap-2 mt-10">
          <button
            onClick={() => setIndex(0)}
            aria-label="Voir Radioclinic"
            className={index === 0 ? 'h-1.5 w-6 rounded-full bg-zinc-900 transition-all duration-300' : 'h-1.5 w-1.5 rounded-full bg-zinc-300 transition-all duration-300'}
            data-testid="hero-visual-dot-0"
          />
          <button
            onClick={() => setIndex(1)}
            aria-label="Voir KIMIA"
            className={index === 1 ? 'h-1.5 w-6 rounded-full bg-zinc-900 transition-all duration-300' : 'h-1.5 w-1.5 rounded-full bg-zinc-300 transition-all duration-300'}
            data-testid="hero-visual-dot-1"
          />
        </div>
      </div>
    </div>
  );
}

export default function HeroSection() {
  const { t } = useTranslation();

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-r from-white/60 via-white/10 to-transparent z-10 hidden lg:block" />

      <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          {/* Visual — first on mobile, right on desktop */}
          <div className="order-1 lg:order-2">
            <HeroVisual />
          </div>

          {/* Text — second on mobile, left on desktop */}
          <div className="order-2 lg:order-1 max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full border border-zinc-200 bg-white/70 backdrop-blur"
            >
              <Sparkles className="w-3.5 h-3.5 text-sky-500" />
              <span className="font-outfit text-[11px] uppercase tracking-[0.18em] text-zinc-600 font-semibold">
                {t('hero.tagline')}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-outfit font-semibold text-[2.5rem] sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-zinc-950 mb-6"
            >
              {t('hero.title')}<br />
              <span className="text-gradient">{t('hero.titleHighlight')}</span>{' '}
              {t('hero.titleEnd')}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="font-manrope text-base md:text-lg text-zinc-600 max-w-xl mb-9 leading-relaxed"
            >
              {t('hero.subtitle')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-3"
            >
              <button
                onClick={() => scrollToSection('#contact')}
                className="group flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-zinc-950 text-white font-medium text-[15px] hover:bg-zinc-800 transition-colors shadow-[0_8px_24px_-8px_rgba(16,24,40,0.4)]"
                data-testid="hero-cta-primary"
              >
                {t('hero.cta')}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>

              <button
                onClick={() => scrollToSection('#services')}
                className="flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-900 font-medium text-[15px] transition-colors"
                data-testid="hero-cta-secondary"
              >
                {t('hero.ctaSecondary')}
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
